import { useState, useEffect } from "react";
import { callConvertTransactionToCSV, callGetBocTransactions } from "../../../../../services/boc/BocController";
import TransactionsTable from "./TrasnactionTable";
import { Button, Typography, Box, Paper, CircularProgress } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TextField } from "@mui/material";
import { useTheme } from "@emotion/react";

const TransactionsPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [nextPageToken, setNextPageToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [numberOfRecords, setNumberOfRecords] = useState(0);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const theme = useTheme();

    const fetchTransactions = async (startDate = null, endDate = null, pageToken = null) => {
        setLoading(true);
        try {
            const result = await callGetBocTransactions({
                startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
                endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
                nextPageToken: pageToken,
            });
             const {
                transactions,
                nextPageToken,
                numberOfRecordsOnThisPage,
                morePages,
            } = result.response;

            setTransactions(transactions);
            setNextPageToken(nextPageToken);
            setNumberOfRecords(numberOfRecordsOnThisPage);
            setHasMorePages(morePages);
            setLoading(false);

        } catch (err) {
            setLoading(false);
            console.error(err);
        }
    };
    const convertToCSV = async (startDate = null, endDate = null, pageToken = null) => {
        setLoading(true);
        try {
            const result = await callConvertTransactionToCSV({
                startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
                endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
                nextPageToken: pageToken,
                exportToCSV: true,
            });
            // Check for empty transaction results
            if (!result?.csv || result.csv.trim().length === 0) {
                setLoading(false);
                alert("No transactions found for this date range.");
                return;
            }
 
            const csvString = result?.csv;
            const fileName = result?.fileName || "transactions.csv";

            if (!csvString) {
                setLoading(false);
                console.error("No CSV returned");
                return;
            }

            // Convert string → Blob
            const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

            // Create download link
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);

            // Append link, click, remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setLoading(false);

        } catch (err) {
            setLoading(false);
            console.error(err);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);
    const cancelFilter = () => {
        setStartDate(null);
        setEndDate(null);
        fetchTransactions();
    }
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>

            <div>
                <Typography mt={2} mb={2} variant="h2">
                    Transactions
                </Typography>

                <Box display="flex" gap={2} mb={3}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 2,
                            width: "100%",
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            backgroundColor: theme.palette.background.dark,
                            alignItems: "center",
                        }}
                    >
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={!!startDate && endDate && dayjs(startDate).isAfter(endDate)}
                                    helperText={
                                        startDate && endDate && dayjs(startDate).isAfter(endDate)
                                            ? "Start date cannot be after End date"
                                            : ""
                                    }
                                />
                            )}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#fff", // default border color
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#fff", // hover border color
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#fff", // focused border color
                                    },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#fff !important", // force white border always
                                },
                                "& .MuiInputBase-input": {
                                    color: "#000", // text color
                                },
                            }}
                            maxDate={dayjs()}
                        />

                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={!!startDate && endDate && dayjs(startDate).isAfter(endDate)}
                                    helperText={
                                        startDate && endDate && dayjs(startDate).isAfter(endDate)
                                            ? "End date cannot be before Start date"
                                            : ""
                                    }
                                />
                            )}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#fff", // default border color
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#fff", // hover border color
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#fff", // focused border color
                                    },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#fff !important", // force white border always
                                },
                                "& .MuiInputBase-input": {
                                    color: "#000", // text color
                                },
                            }}
                            maxDate={dayjs()}
                        />
                        <Button
                            variant="contained"
                            onClick={() => fetchTransactions(startDate, endDate)}
                            disabled={loading || !startDate || !endDate}
                        >
                            Apply Filter
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {

                                cancelFilter();
                            }}
                            disabled={loading || (!startDate && !endDate)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => convertToCSV(startDate, endDate)}
                            disabled={loading || !startDate || !endDate}
                        >
                            Export CSV
                        </Button>
                    </Paper>

                </Box>

                {loading ? <CircularProgress /> : <TransactionsTable transactions={transactions} />}
                <Typography variant="h5" mt={2} mb={2}>
                    Showing {numberOfRecords} record(s) on this page
                </Typography>

                <Button
                    variant="outlined"
                    onClick={() => fetchTransactions(nextPageToken)}
                    disabled={loading || !hasMorePages}
                >
                    {loading ? "Loading..." : hasMorePages ? "Next Page" : "No More Pages"}
                </Button>
            </div>
        </LocalizationProvider >
    );
};

export default TransactionsPage;
